import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { SubmissionDetailsHeaderData } from "../../submission-details.model";
import { BrokerStatus } from "@partner/shared/SharedConstants";
import { Address, AppBarAction, ButtonColor, EIN_VALIDATOR_PATTERN, PHONE_VALIDATOR_PATTERN, ValidatorProvider, ZIP_VALIDATOR_PATTERN, SSN_VALIDATOR_PATTERN, DocumentCategory, AdvisorData, SimpleDialogData, CommonActionKey } from "common";
import { BusinessInformationFormData, DocumentsSectionData, FormDetailsActionType, OwnerInformationFormData, SubmissionFormData } from "./form-details.model";
import { AccountData, ApplicationData, ApplicationDocumentData, ApplicationDocumentsQueryResult, ContactData } from '../../../../domain/models/application.model';
import { Injectable } from '@angular/core';
import { FormDetailsSectionHeaderConfig, FormDetailsSectionHeaderIconType } from './shared/form-details-section-header/form-details-section-header.model';
import { RequirementData, RequirementDocumentData, RequirementsIndicatorType } from '../../shared/components/requirement-documents-section/requirement-documents-panel.model';
import { getContactString } from '../../submission-details.data';
import { SubmissionDetailsBannerData } from '../../shared/components/submission-details-banner-section/submission-details-banner-section.model';

export const MAX_BANK_STATEMENT_COUNT_ON_NEW: number = 6;
export const MAX_BANK_STATEMENT_COUNT_ON_INCOMPLETE: number = 24;
export const MAX_SIGNED_APPLICATION_COUNT_ON_NEW: number = 4;
export const MAX_SIGNED_APPLICATION_COUNT_ON_INCOMPLETE: number = 24;

export const getFormDetailsHeaderData = (brokerStatus: BrokerStatus, advisorData: AdvisorData): SubmissionDetailsHeaderData => {
  switch (brokerStatus) {
    case BrokerStatus.DRAFT:
      return getDraftDetailsHeaderData();
    case BrokerStatus.SUBMISSION_INCOMPLETE:
      return getIncompleteDetailsHeaderData(advisorData);

    default:
      return { title: "", subtitle: "" };
  }
}

const getDraftDetailsHeaderData = (): SubmissionDetailsHeaderData => {
  return {
    title: "Submission",
    subtitle: `Gather and enter all information about your client and submit for review. A company name is required in order to save a draft.`,
  }
}

const getIncompleteDetailsHeaderData = (advisorData: AdvisorData): SubmissionDetailsHeaderData => {
  const contactString = getContactString(advisorData);
  return {
    title: "Complete your submission",
    subtitle: `Provide the required information below so we can move forward with this submission. ${contactString}`,
  }
}

export const getFormDetailsAppBarActions = (): AppBarAction[] => {
  return [{
    id: FormDetailsActionType.SubmitForReview,
    label: "SUBMIT FOR REVIEW",
    buttonType: "button",
    buttonAppearance: "flat",
    buttonColor: "primary",
  }]
}

export const getUnableToSaveDialogData = (): SimpleDialogData => {
  return {
    body: `We were unable to save your submission due to:<ul><li>Company name is required</li></ul><br>Do you want to exit without saving the latest changes?`,
    title: "Unable to save submission",
    actions: [
      {
        label: "YES",
        buttonColor: ButtonColor.Default,
        actionKey: CommonActionKey.Yes,
      },
      {
        label: "NO",
        buttonColor: ButtonColor.Primary,
        actionKey: CommonActionKey.No,
      }
    ]
  }
}

export const getThankYouDialogData = (advisorData: AdvisorData): SimpleDialogData => {
  const contactString = getContactString(advisorData);

  return {
    body: `<div style="line-height: 24px">We are currently reviewing the application and will reach out to you shortly. ${contactString}</div>`,
    title: "Thanks for your submission",
    config: { width: "525px" },
  }
}

export const getSubmissionDetailsBannerData = (application: ApplicationData): SubmissionDetailsBannerData | undefined => {
  if (application.brokerStatus !== BrokerStatus.SUBMISSION_INCOMPLETE || !application.incompleteInfo) {
    return undefined;
  }

  const note = "Note: This submission contains files received via alternative method outside broker portal.";

  return {
    title: "Missing information",
    description: `${application.incompleteInfo}`,
    note: application.hasOutsideFiles ? note : undefined
  }
}

export const getIncompleteSubmissionDialogData = (incompleteInfo?: string): SimpleDialogData => {
  const incompleteInfoString = incompleteInfo ? `<br><br>${incompleteInfo}` : '';
  return {
    body: `Provide the required information below so we can move forward with this submission.${incompleteInfoString}`,
    title: "Complete your submission",
  }
}

export const businessSectionHeaderConfig: FormDetailsSectionHeaderConfig = {
  defaultIconNumber: 1,
  title: "Business information",
  warningSubtitle: "Information required"
}

export const ownersSectionHeaderConfig: FormDetailsSectionHeaderConfig = {
  defaultIconNumber: 2,
  title: "Owner information",
  warningSubtitle: "Information required"
}

export const documentsSectionHeaderConfig: FormDetailsSectionHeaderConfig = {
  defaultIconNumber: 3,
  title: "Documents and cash flow",
  warningSubtitle: "Information required"
}

export const documentsSectionBlockedReason: string = "A company name is required in order to upload documents."

export const getSubmissionForm = (initialValues?: SubmissionFormData): FormGroup => {
  return new FormGroup({
    businessInformation: getBusinessInformationFormGroup(initialValues?.businessInformation),
    ownersInformation: getOwnerInformationFormArray(initialValues?.ownersInformation)
  });
};

const getBusinessInformationFormGroup = (initialValues?: BusinessInformationFormData): FormGroup => {
  return new FormGroup({
    name: new FormControl(initialValues?.name ?? undefined, {
      validators: [Validators.required, Validators.maxLength(256)],
    }),
    ein: new FormControl(initialValues?.ein ?? undefined, {
      validators: [Validators.required, Validators.pattern(EIN_VALIDATOR_PATTERN)],
    }),
    dba: new FormControl(initialValues?.dba ?? undefined, {
      validators: Validators.maxLength(120),
    }),
    entityType: new FormControl(initialValues?.entityType ?? undefined, {
      validators: [Validators.required],
    }),
    phone: new FormControl(initialValues?.phone ?? undefined, {
      validators: [Validators.required, Validators.pattern(PHONE_VALIDATOR_PATTERN)],
    }),
    address: getAddressFormGroup(initialValues?.address),
    timeInBusiness: new FormControl(initialValues?.timeInBusiness ?? undefined, {
      validators: [Validators.required],
    }),
    description: new FormControl(initialValues?.description ?? undefined, {
      validators: [Validators.required, Validators.maxLength(32000)],
    }),
    monthlySales: new FormControl(initialValues?.monthlySales ?? undefined, {
      validators: [Validators.min(1), Validators.max(9999999999.99)],
    }),
    requestedAmount: new FormControl(initialValues?.requestedAmount ?? undefined, {
      validators: [Validators.min(1), Validators.max(9999999999.99)],
    }),
  });
};

export const getOwnerInformationFormArray = (initialValues: OwnerInformationFormData[] = []): FormArray => {
  if (initialValues.length > 0) {
    return new FormArray(
      initialValues.map(owner => getOwnerInformationFormGroup(owner))
    );
  }
  return new FormArray([getOwnerInformationFormGroup()]);
};

export const getOwnerInformationFormGroup = (initialValues?: OwnerInformationFormData): FormGroup => {
  return new FormGroup({
    id: new FormControl(initialValues?.id ?? undefined),
    tempUuid: new FormControl(undefined),
    canRemove: new FormControl(undefined),
    createdOn: new FormControl(initialValues?.createdOn ? new Date(initialValues?.createdOn) : undefined),
    firstName: new FormControl(initialValues?.firstName ?? undefined, {
      validators: [Validators.required, Validators.maxLength(40)],
    }),
    lastName: new FormControl(initialValues?.lastName ?? undefined, {
      validators: [Validators.required, Validators.maxLength(80)],
    }),
    email: new FormControl(initialValues?.email ?? undefined, {
      validators: [Validators.required, ...ValidatorProvider.salesforceEmailValidators()],
    }),
    mobilePhone: new FormControl(initialValues?.mobilePhone ?? undefined, {
      validators: [Validators.required, Validators.pattern(PHONE_VALIDATOR_PATTERN)],
    }),
    address: getAddressFormGroup(initialValues?.address),
    birthdate: new FormControl(initialValues?.birthdate ? new Date(initialValues?.birthdate) : undefined, {
      validators: [Validators.required], updateOn: 'blur'
    }),
    ssn: new FormControl(initialValues?.ssn ?? undefined, {
      validators: [Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern(SSN_VALIDATOR_PATTERN)],
    }),
  });
};

const getAddressFormGroup = (initialValues?: Address): FormGroup => {
  return new FormGroup({
    line1: new FormControl(initialValues?.line1 ?? undefined, {
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(128)],
    }),
    line2: new FormControl(initialValues?.line2 ?? undefined, {
      validators: [Validators.maxLength(16)],
    }),
    city: new FormControl(initialValues?.city ?? undefined, {
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(40)],
    }),
    state: new FormControl(initialValues?.state ?? undefined, {
      validators: [Validators.required],
    }),
    zip: new FormControl(initialValues?.zip ?? undefined, {
      validators: [Validators.required, Validators.pattern(ZIP_VALIDATOR_PATTERN)],
    }),
  });
};

@Injectable()
export class FormDetailsAdapter {
  adaptFormData = (application: ApplicationData, account?: AccountData, contacts?: ContactData[]): SubmissionFormData | undefined => {
    if (!application || !account) {
      return undefined;
    }

    return {
      businessInformation: {
        name: account.name,
        ein: account.ein,
        entityType: account.entityType,
        phone: account.phone,
        address: account.address,
        dba: account.dba,
        timeInBusiness: account.timeInBusiness,
        description: account.description,
        monthlySales: account.monthlySales,
        requestedAmount: application.requestedAmount,
      },
      ownersInformation: contacts?.map(contact => ({
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        mobilePhone: contact.mobilePhone,
        address: contact.address,
        birthdate: contact.birthdate ? new Date(contact.birthdate) : "",
        ssn: contact?.ssn?.replaceAll("-", ""),
        createdOn: contact.createdOn ? new Date(contact.createdOn) : undefined
      }))
    }
  }

  adaptDocumentsSectionData = (applicationDocuments?: ApplicationDocumentsQueryResult, application?: ApplicationData): DocumentsSectionData => {
    const requirements: RequirementData[] = this.adaptRequirementsData(applicationDocuments, application);

    return {
      requirements,
      hasAnyDocumentChanges: false,
      headerConfig: documentsSectionHeaderConfig,
      headerIconType: FormDetailsSectionHeaderIconType.Default,
      isValid: requirements.every(item => item.isValid),
    }
  }

  private adaptRequirementsData = (applicationDocuments: ApplicationDocumentsQueryResult, application: ApplicationData): RequirementData[] => {
    const bankStatementsDocs = applicationDocuments?.values?.filter(doc => doc.category === DocumentCategory.BankStatement) ?? [];
    const signedApplicationDocs = applicationDocuments?.values?.filter(doc => doc.category === DocumentCategory.Application) ?? [];

    const bankStatementRequirementDocs = this.adaptRequirementDocumentsData(bankStatementsDocs);
    const signedApplicationRequirementDocs = this.adaptRequirementDocumentsData(signedApplicationDocs);
    const maxBankStatementDocumentsCount = this.getMaxBankStatementDocumentsCount(application?.brokerStatus);
    const maxSignedAppDocumentsCount = this.getMaxSignedAppDocumentsCount(application?.brokerStatus);
    const maxBankStatementDocumentsCountToUpload = this.getMaxDocumentsCountToUpload(maxBankStatementDocumentsCount, bankStatementRequirementDocs?.length);
    const maxSignedAppDocumentsCountToUpload = this.getMaxDocumentsCountToUpload(maxSignedAppDocumentsCount, signedApplicationRequirementDocs?.length);

    return [
      {
        requirementId: 1,
        indicatorType: this.getIndicatorType(bankStatementRequirementDocs, application?.hasOutsideFiles),
        name: this.getBankStatementRequirementName(application?.hasOutsideFiles),
        documents: bankStatementRequirementDocs,
        description: this.getRequirementDescription(maxBankStatementDocumentsCount, maxBankStatementDocumentsCountToUpload, application?.hasOutsideFiles),
        isUploadActionVisible: maxBankStatementDocumentsCountToUpload > 0,
        maxDocumentsCountToUpload: maxBankStatementDocumentsCountToUpload,
        category: DocumentCategory.BankStatement,
        isValid: this.checkIfIsRequirementValid(application?.hasOutsideFiles, bankStatementRequirementDocs?.length),
        isLoading: false,
        useLoaderWhenDownloading: true,
        useLoaderWhenDeleting: true,
        useLoaderWhenUploading: true,
      },
      {
        requirementId: 2,
        indicatorType: this.getIndicatorType(signedApplicationRequirementDocs, application?.hasOutsideFiles),
        name: this.getSignedApplicationRequirementName(application?.hasOutsideFiles),
        documents: signedApplicationRequirementDocs,
        description: this.getRequirementDescription(maxSignedAppDocumentsCount, maxSignedAppDocumentsCountToUpload, application?.hasOutsideFiles),
        isUploadActionVisible: maxSignedAppDocumentsCountToUpload > 0,
        maxDocumentsCountToUpload: maxSignedAppDocumentsCountToUpload,
        category: DocumentCategory.Application,
        isValid: this.checkIfIsRequirementValid(application?.hasOutsideFiles, signedApplicationRequirementDocs?.length),
        isLoading: false,
        useLoaderWhenDownloading: true,
        useLoaderWhenDeleting: true,
        useLoaderWhenUploading: true,
      },
    ]
  }

  private adaptRequirementDocumentsData = (documents: ApplicationDocumentData[]): RequirementDocumentData[] => {
    return documents?.map(document => {
      return {
        id: document.id,
        name: document.filename,
        isRemovable: document.isTemporary,
        isDownloadable: true,
        isLabelVisible: document.isTemporary,
      }
    })
  }

  private getMaxBankStatementDocumentsCount(brokerStatus: BrokerStatus): number {
    if (brokerStatus === BrokerStatus.SUBMISSION_INCOMPLETE) {
      return MAX_BANK_STATEMENT_COUNT_ON_INCOMPLETE;
    }
    return MAX_BANK_STATEMENT_COUNT_ON_NEW;
  }

  private getMaxSignedAppDocumentsCount(brokerStatus: BrokerStatus): number {
    if (brokerStatus === BrokerStatus.SUBMISSION_INCOMPLETE) {
      return MAX_SIGNED_APPLICATION_COUNT_ON_INCOMPLETE;
    }
    return MAX_SIGNED_APPLICATION_COUNT_ON_NEW;
  }

  private getMaxDocumentsCountToUpload(maxCount: number, currentDocumentsLength?: number): number {
    const max = maxCount - (currentDocumentsLength ?? 0);
    return max > 0 ? max : 0;
  }

  private getIndicatorType(documents: RequirementDocumentData[], hasOutsideFiles: boolean): RequirementsIndicatorType {
    return documents?.length > 0 || hasOutsideFiles ? RequirementsIndicatorType.InProgress : RequirementsIndicatorType.Incomplete;
  }

  private getBankStatementRequirementName(hasOutsideFiles: boolean): string {
    return hasOutsideFiles ? "Last 3 months bank statements (Optional)" : "Last 3 months bank statements (Required) *";
  }

  private getSignedApplicationRequirementName(hasOutsideFiles: boolean): string {
    return hasOutsideFiles ? "Signed Application (Optional)" : "Signed Application (Required) *";
  }

  private getRequirementDescription(maxDocumentsCount: number, maxDocumentsCountToUpload: number, hasOutsideFiles: boolean): string {
    if (hasOutsideFiles) {
      return "Received via alternative method outside Broker Portal.";
    }

    const isUploadAvailable = this.checkIfIsUploadActionAvailable(maxDocumentsCountToUpload);
    return isUploadAvailable ? `Upload any PNG, JPEG, JPG, GIF, TIFF, TIF, PDF copies (up to ${maxDocumentsCount} files).` : "";
  }

  private checkIfIsUploadActionAvailable(maxDocumentsCountToUpload: number): boolean {
    return maxDocumentsCountToUpload > 0;
  }

  private checkIfIsRequirementValid(hasOutsideFiles: boolean, documentsLength: number): boolean {
    return hasOutsideFiles || documentsLength > 0;
  }
}
